// Kênh thời gian thực (SSE qua fetch để gửi Authorization trong header). Tự kết nối lại có lùi dần;
// 401 ⇒ làm mới token rồi nối lại. Tin chỉ là gợi ý "dữ liệu đã đổi" — giao diện tải lại qua API.
// Bổ sung (FSD.md §4.1): trạng thái kênh, sự kiện `reconnected`, đăng ký chủ đề ⇒ khoá cần làm mới (RealtimeBinding).
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { API_BASE } from '../config';
import { getAccessToken, refreshSession } from './http';
import type { AnyQueryKey } from './query-keys';

type Handler = (data: unknown) => void;
const handlers = new Map<string, Set<Handler>>();
let ctrl: AbortController | null = null;
let running = false;
/** Mỗi lần startRealtime một thế hệ — vòng lặp cũ (đang chờ lùi) tự dừng khi đăng nhập lại. */
let generation = 0;

// ---------- Trạng thái kênh ----------
export type RealtimeStatus = 'connecting' | 'open' | 'closed';
let status: RealtimeStatus = 'closed';
const statusListeners = new Set<() => void>();
function setStatus(s: RealtimeStatus) {
  if (status === s) return;
  status = s;
  statusListeners.forEach((l) => l());
}
export const getRealtimeStatus = () => status;
const subscribeStatus = (l: () => void) => {
  statusListeners.add(l);
  return () => void statusListeners.delete(l);
};
/** 'open' khi đang nhận tin; khác 'open' ⇒ trang bật hỏi lại theo chu kỳ (bảng hàng 5 s…). */
export const useRealtimeStatus = () => useSyncExternalStore(subscribeStatus, getRealtimeStatus, getRealtimeStatus);

/** Đã mở kênh ít nhất một lần kể từ startRealtime ⇒ lần mở sau là "nối lại" (có thể đã lỡ tin). */
let openedOnce = false;
const reconnectListeners = new Set<() => void>();
/** Nghe sự kiện nối lại sau mất kết nối (không phát ở lần mở đầu tiên). Trả hàm huỷ. */
export function onReconnected(l: () => void) {
  reconnectListeners.add(l);
  return () => void reconnectListeners.delete(l);
}

/**
 * Gọi từng callback độc lập: lỗi của một trang/binding không được làm rơi kết nối SSE (vòng lặp chỉ nối lại khi lỗi
 * đến từ fetch/đọc luồng) và không chặn các callback còn lại.
 */
function safely<A extends unknown[]>(fn: (...a: A) => void, ...args: A) {
  try {
    fn(...args);
  } catch (e) {
    console.error('[realtime]', e);
  }
}

function dispatch(topic: string, raw: string) {
  let data: unknown = {};
  try {
    data = raw ? JSON.parse(raw) : {};
  } catch {
    data = raw;
  }
  handlers.get(topic)?.forEach((h) => safely(h, data));
  handlers.get('*')?.forEach((h) => safely(h, { topic, data }));
}

async function loop(gen: number) {
  const alive = () => running && gen === generation;
  let backoff = 1000;
  while (alive()) {
    ctrl = new AbortController();
    setStatus('connecting');
    try {
      const r = await fetch(`${API_BASE}/stream`, { headers: { Accept: 'text/event-stream', Authorization: `Bearer ${getAccessToken() ?? ''}` }, signal: ctrl.signal });
      if (r.status === 401) {
        const ok = await refreshSession();
        // Trong lúc chờ làm mới có thể đã đăng xuất rồi đăng nhập lại (thế hệ mới) ⇒ vòng cũ không được dừng thế hệ mới
        if (!alive()) return;
        if (!ok) return stopRealtime();
        continue;
      }
      if (!r.ok || !r.body) throw new Error(String(r.status));
      backoff = 1000;
      setStatus('open');
      if (openedOnce) reconnectListeners.forEach((l) => safely(l));
      openedOnce = true;
      const reader = r.body.pipeThrough(new TextDecoderStream()).getReader();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += value;
        let i: number;
        while ((i = buf.indexOf('\n\n')) >= 0) {
          const block = buf.slice(0, i);
          buf = buf.slice(i + 2);
          let event = 'message';
          const data: string[] = [];
          for (const line of block.split('\n')) {
            if (line.startsWith('event:')) event = line.slice(6).trim();
            else if (line.startsWith('data:')) data.push(line.slice(5).trimStart());
          }
          if (event !== 'ping') dispatch(event, data.join('\n'));
        }
      }
    } catch (e) {
      if ((e as Error).name === 'AbortError' && !alive()) return;
    }
    if (!alive()) return;
    setStatus('closed');
    await new Promise((res) => setTimeout(res, backoff));
    backoff = Math.min(backoff * 2, 30_000);
  }
}

export function startRealtime() {
  if (running) return;
  running = true;
  openedOnce = false;
  void loop(++generation);
}
export function stopRealtime() {
  running = false;
  ctrl?.abort();
  setStatus('closed');
}

function listen(topic: string, h: Handler) {
  const set = handlers.get(topic) ?? new Set();
  set.add(h);
  handlers.set(topic, set);
  return () => void set.delete(h);
}

/** Nghe một chủ đề ('booking.units', 'inbox'…; '*' = mọi chủ đề, nhận { topic, data }). */
export function useRealtime(topic: string, handler: Handler) {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => listen(topic, (d) => ref.current(d)), [topic]);
}

// ---------- Chủ đề ⇒ khoá cần làm mới ----------
export interface RealtimeBinding {
  topic: string;
  /** Module khai báo (chỉ đăng ký khi module bật). */
  module: string;
  /** Khoá (tiền tố) cần làm mới với dữ liệu gợi ý của tin; gọi với {} khi nối lại kênh. */
  keys: (data: { ids?: string[] }) => readonly AnyQueryKey[];
  /** Gộp tin trong cửa sổ này rồi làm mới một lần ở cạnh sau (mặc định 300 ms). */
  throttleMs?: number;
}

const hintOf = (data: unknown): { ids?: string[] } => {
  if (!data || typeof data !== 'object') return {};
  const ids = (data as { ids?: unknown }).ids;
  return Array.isArray(ids) ? { ids: ids.map(String) } : {};
};
const keyId = (k: AnyQueryKey) => JSON.stringify(k);
/** keys() của một binding lỗi ⇒ bỏ binding đó (ghi console), các binding khác vẫn chạy. */
function keysOf(b: RealtimeBinding, hint: { ids?: string[] }): readonly AnyQueryKey[] {
  try {
    return b.keys(hint);
  } catch (e) {
    console.error(`[realtime] binding ${b.module}/${b.topic}`, e);
    return [];
  }
}
const documentHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

/**
 * Bộ gộp tin thuần (không phụ thuộc React) — tách ra để kiểm thử: mỗi binding một cửa sổ gộp; tab ẩn ⇒ chỉ ghi nhớ khoá,
 * làm mới một lần khi tab hiện lại.
 */
export function createBindingScheduler(invalidate: (key: AnyQueryKey) => void, isHidden: () => boolean = documentHidden) {
  const pending = new Map<RealtimeBinding, { keys: Map<string, AnyQueryKey>; timer: ReturnType<typeof setTimeout> }>();
  const deferred = new Map<string, AnyQueryKey>();
  const run = (keys: Map<string, AnyQueryKey>) => {
    if (isHidden()) keys.forEach((k, id) => deferred.set(id, k));
    else keys.forEach((k) => invalidate(k));
  };
  return {
    /** Một tin của chủ đề `b.topic`. */
    push(b: RealtimeBinding, data: unknown) {
      let p = pending.get(b);
      if (!p) {
        const keys = new Map<string, AnyQueryKey>();
        p = {
          keys,
          timer: setTimeout(() => {
            pending.delete(b);
            run(keys);
          }, b.throttleMs ?? 300),
        };
        pending.set(b, p);
      }
      for (const k of keysOf(b, hintOf(data))) p.keys.set(keyId(k), k);
    },
    /** Nối lại kênh ⇒ làm mới một lần mọi khoá đã đăng ký (bù tin lỡ). */
    all(bindings: readonly RealtimeBinding[]) {
      const keys = new Map<string, AnyQueryKey>();
      for (const b of bindings) for (const k of keysOf(b, {})) keys.set(keyId(k), k);
      run(keys);
    },
    /** Tab hiện lại ⇒ làm mới các khoá đã hoãn. */
    visible() {
      if (!deferred.size) return;
      const keys = [...deferred.values()];
      deferred.clear();
      keys.forEach((k) => invalidate(k));
    },
    dispose() {
      pending.forEach((p) => clearTimeout(p.timer));
      pending.clear();
      deferred.clear();
    },
  };
}

/** Gắn mọi binding ở khung ứng dụng (không phụ thuộc trang đang mở ⇒ cache của trang chưa mở cũng được đánh dấu cũ). */
export function useRealtimeBindings(bindings: readonly RealtimeBinding[]) {
  const qc = useQueryClient();
  const ref = useRef(bindings);
  ref.current = bindings;
  useEffect(() => {
    const s = createBindingScheduler((queryKey) => void qc.invalidateQueries({ queryKey }));
    const off = listen('*', (msg) => {
      const { topic, data } = msg as { topic: string; data: unknown };
      for (const b of ref.current) if (b.topic === topic) s.push(b, data);
    });
    const offReconnect = onReconnected(() => s.all(ref.current));
    const onVisibility = () => document.visibilityState === 'visible' && s.visible();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      off();
      offReconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      s.dispose();
    };
  }, [qc]);
}
